/**
 * Side-by-side before/after 3D preview for the model optimizer, built on the
 * real <trace-viewer> web component (the same renderer end users see).
 *
 * The two viewers are mounted ONCE into the stable `#preview-slot` (which
 * renderApp never rewrites) so their WebGL contexts survive app re-renders and
 * the elements are never re-parented (which would dispose them).
 */
import 'trace-viewer'; // registers the <trace-viewer> custom element
import type { TraceViewerElement } from 'trace-viewer';
import type { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import type { AxisConversion } from 'trace-viewer/model-utils';

interface Pane {
  viewer: TraceViewerElement;
}

/** A captured orbit-camera pose, restorable across a model reload. */
interface CameraPose {
  alpha: number;
  beta: number;
  radius: number;
  target: { x: number; y: number; z: number };
}

export class PreviewViewer {
  private before: Pane | null = null;
  private after: Pane | null = null;
  private ready: Promise<void> | null = null;
  private slot: HTMLElement | null = null;
  /** The source File the "before" pane was loaded from (for re-orienting). */
  private beforeSource: File | null = null;
  /** Axis conversion currently reflected in the "before" pane. */
  private beforeConversion: AxisConversion = 'none';
  /** Uniform scale currently reflected in the "before" pane. */
  private beforeScale = 1;
  /** Whether the "after" pane currently holds an optimized model. */
  private afterLoaded = false;
  /** Guards the camera-sync handlers against feedback loops. */
  private syncing = false;
  private syncWired = false;

  /**
   * Mount the two viewers into the stable preview slot, SERIALLY (two engines
   * racing to connect collide on Babylon globals and one rejects whenReady()).
   * Idempotent: only builds once.
   */
  private mount(slot: HTMLElement): Promise<void> {
    if (this.ready) return this.ready;
    this.slot = slot;

    // Layout lives in styles.css (.preview-grid / .preview-pane); the slot's
    // `hidden` attribute drives visibility.
    const makeCol = (label: string): HTMLElement => {
      const col = document.createElement('div');
      col.className = 'preview-pane';
      col.innerHTML = `<span class="preview-pane-label">${label}</span>`;
      return col;
    };
    const beforeCol = makeCol('Original');
    const afterCol = makeCol('Optimized');
    slot.append(beforeCol, afterCol);

    this.ready = (async () => {
      const beforeViewer = this.makeViewer('before');
      beforeCol.appendChild(beforeViewer);
      await beforeViewer.whenReady();
      this.before = { viewer: beforeViewer };

      const afterViewer = this.makeViewer('after');
      afterCol.appendChild(afterViewer);
      await afterViewer.whenReady();
      this.after = { viewer: afterViewer };
    })();

    return this.ready;
  }

  private makeViewer(pane: 'before' | 'after'): TraceViewerElement {
    const v = document.createElement('trace-viewer') as TraceViewerElement;
    v.setAttribute('data-pane', pane);
    // Edit-mode (play-mode off) so animations/behaviors don't auto-run — the
    // preview is for visual comparison, not playback. Both panes match.
    v.setAttribute('play-mode', 'false');
    return v;
  }

  /** Mount into the slot (if present) and reveal it. */
  show(slot: HTMLElement): void {
    void this.mount(slot);
    slot.hidden = false;
  }

  hide(): void {
    if (this.slot) this.slot.hidden = true;
  }

  /**
   * Empty both panes and forget the source. Call when a NEW input file arrives:
   * if that file fails to probe (or its first optimize pass fails), the previous
   * model's optimized result would otherwise stay on screen, reading as if it
   * belonged to the file just dropped.
   */
  async clear(): Promise<void> {
    await this.ready;
    this.beforeSource = null;
    this.beforeConversion = 'none';
    this.beforeScale = 1;
    this.afterLoaded = false;
    // clearScene() keeps the camera and WebGL context alive — only the content goes.
    this.before?.viewer.clearScene();
    this.after?.viewer.clearScene();
  }

  /**
   * Load the original source into the "before" pane (raw, no conversion). Call
   * once on file load; later use `setBeforeConversion` to re-orient without
   * re-loading from scratch.
   */
  async loadBefore(source: File): Promise<void> {
    await this.ready;
    this.beforeSource = source;
    this.beforeConversion = 'none';
    this.beforeScale = 1;
    if (this.before) await this.loadInto(this.before, source, false);
    this.wireCameraSync();
  }

  /**
   * Ensure the "before" pane reflects the given axis conversion and uniform
   * scale, so it matches the optimized output. No-op if both already match — so
   * a re-run that didn't change orientation or scale never reloads the input
   * preview. `convert` re-encodes the source GLB with the conversion + scale
   * baked in (the only reliable way the viewer honors them).
   */
  async setBeforeConversion(
    conversion: AxisConversion,
    convert: (src: File, c: AxisConversion, scale: number) => Promise<Blob>,
    scale = 1,
  ): Promise<void> {
    if (
      !this.beforeSource ||
      (conversion === this.beforeConversion && scale === this.beforeScale)
    ) {
      return;
    }
    await this.ready;
    const blob = await convert(this.beforeSource, conversion, scale);
    this.beforeConversion = conversion;
    this.beforeScale = scale;
    if (this.before) await this.loadInto(this.before, blob, false);
    this.wireCameraSync();
  }

  /**
   * Load a GLB blob into the "after" (optimized) pane.
   * @param preserveCamera keep the current view instead of auto-framing.
   */
  async loadAfter(blob: Blob, preserveCamera = false): Promise<void> {
    await this.ready;
    if (this.after) await this.loadInto(this.after, blob, preserveCamera);
    this.afterLoaded = true;
    this.wireCameraSync();
  }

  /**
   * True once both panes have a model loaded — i.e. a re-run can preserve view.
   * Tracked explicitly rather than inferred from the pane's camera, which
   * survives `clearScene()` and would keep reporting content after a clear.
   */
  get hasContent(): boolean {
    return this.afterLoaded && !!this.before && !!this.after && !!this.cameraOf(this.after.viewer);
  }

  private async loadInto(pane: Pane, blob: Blob, preserveCamera: boolean): Promise<void> {
    // openBlob routes by filename, frames the model, and starts rendering
    // (component owns framing). To keep the current view across a reload, snapshot
    // the pose from the OTHER pane — its camera is untouched by this reload, so it
    // holds the user's view even while openBlob re-frames (and re-syncs) this one.
    const otherViewer = pane === this.after ? this.before?.viewer : this.after?.viewer;
    const pose = preserveCamera && otherViewer ? this.capturePose(otherViewer) : null;
    await pane.viewer.openBlob(blob, 'model.glb');
    this.fixWheelZoom(pane.viewer);
    if (pose) {
      // Restore both panes (openBlob re-framed this one and the sync handler
      // dragged the other along during the reload).
      this.restorePose(pane.viewer, pose);
      if (otherViewer) this.restorePose(otherViewer, pose);
    }
  }

  private capturePose(viewer: TraceViewerElement): CameraPose | null {
    const cam = this.cameraOf(viewer);
    if (!cam) return null;
    return {
      alpha: cam.alpha,
      beta: cam.beta,
      radius: cam.radius,
      target: { x: cam.target.x, y: cam.target.y, z: cam.target.z },
    };
  }

  private restorePose(viewer: TraceViewerElement, pose: CameraPose): void {
    const cam = this.cameraOf(viewer);
    if (!cam) return;
    cam.alpha = pose.alpha;
    cam.beta = pose.beta;
    cam.radius = pose.radius;
    cam.target.set(pose.target.x, pose.target.y, pose.target.z);
  }

  /**
   * Babylon's attachControl is called with noPreventDefault=true inside the
   * component, so wheel events bubble up as page scroll instead of zooming.
   * Re-attach with preventDefault enabled for this inline preview.
   */
  private fixWheelZoom(viewer: TraceViewerElement): void {
    const cam = this.cameraOf(viewer);
    const canvas = viewer.querySelector('canvas');
    if (!cam || !canvas) return;
    cam.detachControl();
    cam.attachControl(canvas, /* noPreventDefault */ false);
  }

  private cameraOf(viewer: TraceViewerElement): ArcRotateCamera | null {
    return (viewer.scene?.activeCamera as ArcRotateCamera | undefined) ?? null;
  }

  /** Mirror camera movement between the two panes so comparisons stay aligned. */
  private wireCameraSync(): void {
    if (this.syncWired) return;
    const a = this.before && this.cameraOf(this.before.viewer);
    const b = this.after && this.cameraOf(this.after.viewer);
    if (!a || !b) return; // both cameras must exist before wiring

    const link = (src: ArcRotateCamera, dst: ArcRotateCamera): void => {
      src.onViewMatrixChangedObservable.add(() => {
        if (this.syncing) return;
        this.syncing = true;
        dst.alpha = src.alpha;
        dst.beta = src.beta;
        dst.radius = src.radius;
        dst.target.copyFrom(src.target);
        this.syncing = false;
      });
    };
    link(a, b);
    link(b, a);
    this.syncWired = true;
  }
}

/** Singleton — there is only ever one preview area on the page. */
export const previewViewer = new PreviewViewer();
